import { Details, AssessmentTabs, Scorecard } from '..';

import { Button, Flex, Icon } from '@chakra-ui/react';
import { useMemo, useRef, useState } from 'react';
import { CenterSpinner } from '@/components/ats';
import { useShowToast } from '@/hooks';
import { KDrawer } from '@/ui-library/components/molecules/KDrawer';
import { KText } from '@/ui-library/components/text';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ASSESSMENT_API } from '../api/codingAssessment.api';
import { getErrorMessage } from '@/utils/errors';
import {
  Hackerrank,
  Hackerearth,
  CriteriaCorp,
  Hireflix,
} from '@/assets/icons/ats';
import { PlatformLogical } from '@/types/ext_assessment';

const PLATFORM_CONFIG = {
  hackerrank: {
    name: 'HackerRank',
    icon: Hackerrank,
    apiKey: 'hackerrank',
  },
  hackerearth: {
    name: 'HackerEarth',
    icon: Hackerearth,
    apiKey: 'hackerearth',
  },
  criteriacorp: {
    name: 'Criteria Corp',
    icon: CriteriaCorp,
    apiKey: 'criteriacorp',
  },
  hireflix: {
    name: 'Hireflix',
    icon: Hireflix,
    apiKey: 'hireflix',
  },
};

interface AssessmentDrawerProps {
  isOpen?: boolean;
  onClose?: () => void;
  activity?: any;
  application?: any;
  stageId?: string;
  refetch?: () => void;
  isAdhoc?: boolean;
  platform: PlatformLogical;
}

export const CodingAssessmentDrawer: React.FC<AssessmentDrawerProps> = ({
  isOpen = true,
  onClose = () => {},
  activity,
  application,
  stageId,
  refetch = () => {},
  isAdhoc = false,
  platform,
}) => {
  const platformConfig = PLATFORM_CONFIG[platform];
  const toast = useShowToast();

  const { data: createData, isLoading: isCreateLoading } = useQuery({
    queryKey: ['createAssessment', activity?.id, application?.id, platform],
    queryFn: () =>
      ASSESSMENT_API.createAssessment({
        ats_job_stage_action_id: activity?.id,
        ats_application_id: application?.id,
        platform,
      }),
    enabled: !!(activity?.id && application?.id && !activity?.data?.id),
    staleTime: 0,
  });

  const assessmentId = activity?.data?.id || createData?.id;

  const { data: assessmentData, isLoading: isGetLoading } = useQuery({
    queryKey: ['getAssessment', assessmentId, platform],
    queryFn: () => ASSESSMENT_API.getAssessment(assessmentId, platform),
    enabled: !!assessmentId,
    staleTime: 0,
  });

  const inviteMode = useMemo(
    () =>
      activity?.data?.status === 'pending' ||
      assessmentData?.status === 'pending',
    [activity?.data?.status, assessmentData?.status]
  );

  const inviteAssessmentMutation = useMutation({
    mutationFn: ASSESSMENT_API.inviteAssessment,
    onSuccess: (result) => {
      // @ts-ignore
      if (result?.data?.id) {
        toast({
          title: inviteMode
            ? `${platformConfig.name} assessment sent successfully`
            : `${platformConfig.name} assessment updated successfully`,
          status: 'success',
        });
        refetch();
        onClose();
      } else {
        toast({
          title: getErrorMessage(result, 'extAssessment'),
          status: 'error',
        });
        onClose();
      }
    },
    onError: (error) => {
      toast({
        title: getErrorMessage(error, 'extAssessment'),
        status: 'error',
      });
      onClose();
    },
  });

  const inviteAdhocAssessmentMutation = useMutation({
    mutationFn: ASSESSMENT_API.inviteAdhocAssessment,
    onSuccess: (result) => {
      // @ts-ignore
      if (result?.success) {
        toast({
          title: `${platformConfig.name} assessment sent successfully`,
          status: 'success',
        });
        refetch();
        onClose();
      } else {
        toast({
          title: getErrorMessage(result, 'extAssessment'),
          status: 'error',
        });
        onClose();
      }
    },
    onError: (error) => {
      toast({
        title: getErrorMessage(error, 'extAssessment'),
        status: 'error',
      });
      onClose();
    },
  });

  const [currentTab, setCurrentTab] = useState('details');
  const isLoading = isCreateLoading || isGetLoading;

  const detailsRef = useRef(null);
  const scorecardRef = useRef(null);

  const assessmentTabs = [
    {
      id: 'details',
      label: 'Details',
      component: Details,
      props: {
        formRef: detailsRef,
        activity: assessmentData || {},
        jobStage: true,
        platform,
      },
    },
    {
      id: 'scorecard',
      label: 'Scorecard',
      component: Scorecard,
      props: {
        ref: scorecardRef,
        note: 'This is the recommended scorecard template. You can customize it but changes will only apply to this assessment.',
        interviewPlan: {
          id: assessmentData?.id,
          scorecard_config: assessmentData?.scorecard,
          ...assessmentData?.scorecard,
        },
        platform,
      },
    },
  ];

  const getNextTab = () => {
    const currentIndex = assessmentTabs.findIndex(
      (tab) => tab.id === currentTab
    );
    return assessmentTabs?.[currentIndex + 1];
  };

  const handleSubmit = async () => {
    if (currentTab === 'details') {
      await detailsRef?.current?.submitForm();
      if (!detailsRef?.current?.isValid) return;

      setCurrentTab('scorecard');
    } else if (currentTab === 'scorecard') {
      const scorecardResult = await scorecardRef?.current?.getModel();

      if (!scorecardResult?.hasValidData) {
        return;
      }

      const detailsValues = detailsRef?.current?.values || {};
      const scorecardPayload = scorecardResult?.payload || {};
      const {
        scorecard_feedback_description,
        scorecard_rating_description,
        scorecard_config,
      } = scorecardPayload;

      const finalParams = {
        ext_test_name: assessmentData?.ext_test_name,
        ext_assessment_link: assessmentData?.ext_assessment_link,
        ...detailsValues,
        feedback_description: scorecard_feedback_description,
        rating_description: scorecard_rating_description,
        scoreCards: scorecard_config?.questions,
        platform,
        kind: platform === 'hackerearth' ? 'hackerearth_assessment' : platform,
      };

      if (isAdhoc) {
        inviteAdhocAssessmentMutation.mutate({
          assessmentId: activity?.data?.id || createData?.id,
          ats_job_stage_id: stageId,
          ats_job_stage_action_id: activity?.id,
          ats_application_id: application?.id,
          params: finalParams,
          platform,
        });
      } else {
        inviteAssessmentMutation.mutate({
          assessmentId: activity?.data?.id || createData?.id,
          ats_job_stage_id: stageId,
          ats_job_stage_action_id: activity?.id,
          ats_application_id: application?.id,
          params: finalParams,
          platform,
        });
      }
    }
  };

  const renderFooter = () => {
    if (isLoading) return null;
    return (
      <Flex
        p="lg"
        borderTop="1px solid"
        borderColor="border-color"
        bg="ats-secondary"
      >
        {currentTab === 'scorecard' && (
          <Button variant="secondary" onClick={() => setCurrentTab('details')}>
            Back
          </Button>
        )}
        <Flex ml="auto" gap="12px">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>

          <Button
            variant="primary"
            onClick={handleSubmit}
            isLoading={
              inviteAssessmentMutation.isPending ||
              inviteAdhocAssessmentMutation.isPending
            }
          >
            {currentTab !== 'scorecard'
              ? `Next: ${getNextTab()?.label} `
              : inviteMode || isAdhoc
              ? 'Send assessment'
              : 'Update assessment'}
          </Button>
        </Flex>
      </Flex>
    );
  };

  const renderHeader = () => {
    if (isLoading) return null;
    return (
      <Flex p="20px 24px 0px 24px">
        <KText size="md-medium">
          {isAdhoc || inviteMode ? 'Send' : 'Update'}
        </KText>
        <Icon
          as={platformConfig.icon}
          w="22px"
          h="22px"
          borderRadius="sm"
          mx="5px"
        />
        <KText size="md-medium">{platformConfig.name} assessment</KText>
      </Flex>
    );
  };

  return (
    <KDrawer
      isOpen={isOpen}
      onClose={onClose}
      footer={renderFooter()}
      header={renderHeader()}
      contentProps={{
        maxW: '700px',
        borderTopLeftRadius: 'xl',
      }}
    >
      <Flex flexDir="column" minH="100%">
        {isLoading ? (
          <CenterSpinner flex="1" />
        ) : (
          <AssessmentTabs
            tabs={assessmentTabs}
            isCreateFlow={false}
            currentTab={currentTab}
            activity={assessmentData}
            onTabChange={setCurrentTab}
            platform={platform}
            onTabValidation={async (fromTab, toTab) => {
              if (fromTab === 'details' && toTab === 'scorecard') {
                await detailsRef?.current?.submitForm();
                return detailsRef?.current?.isValid;
              }
              return true;
            }}
          />
        )}
      </Flex>
    </KDrawer>
  );
};
